import React, { useState, useContext } from "react";
import PlatformCtx from "../../state/PlatformCtx";
import FileTypeIcon from "./FileTypeIcon";
import T from "../../i18n/translations";
import { localName } from "../../utils/helpers";

const FILE_PROVIDERS=[
  {id:"onedrive",name:"OneDrive",    nameAr:"ون درايف",  icon:"▣",color:"#0078D4",comingSoon:false},
  {id:"gdrive",  name:"Google Drive",nameAr:"جوجل درايف",icon:"▲",color:"#4285F4",comingSoon:true},
  {id:"dropbox", name:"Dropbox",     nameAr:"دروب بوكس", icon:"◈",color:"#0061FF",comingSoon:true},
];

/* Mock filesystem — in production replaced by live provider API calls (Microsoft Graph / Google Drive API).
   Nothing here is stored in TR OS state. Each call would be a fresh API request. */
const MOCK_FS={
  root:[
    {id:"f1",name:"Documents",      type:"folder",modified:"2026-03-20",size:null},
    {id:"f2",name:"Projects",       type:"folder",modified:"2026-03-18",size:null},
    {id:"f3",name:"Finance",        type:"folder",modified:"2026-03-15",size:null},
    {id:"f4",name:"Legal",          type:"folder",modified:"2026-03-10",size:null},
    {id:"f5",name:"Q1 Board Presentation.pptx",type:"file",ext:"pptx",modified:"2026-03-19",size:"4.2 MB"},
    {id:"f6",name:"March Budget Overview.xlsx", type:"file",ext:"xlsx",modified:"2026-03-21",size:"890 KB"},
  ],
  f1:[
    {id:"f1a",name:"Contracts",               type:"folder",modified:"2026-03-12",size:null},
    {id:"f1b",name:"Reports",                 type:"folder",modified:"2026-03-08",size:null},
    {id:"f1c",name:"NDA — Al-Faisal.pdf",     type:"file",ext:"pdf", modified:"2026-03-18",size:"320 KB"},
    {id:"f1d",name:"Meeting Notes — March.docx",type:"file",ext:"docx",modified:"2026-03-20",size:"120 KB"},
  ],
  f2:[
    {id:"f2a",name:"Tower B",             type:"folder",modified:"2026-03-14",size:null},
    {id:"f2b",name:"Tower C",             type:"folder",modified:"2026-03-11",size:null},
    {id:"f2c",name:"Project Charter.docx",type:"file",ext:"docx",modified:"2026-03-10",size:"240 KB"},
  ],
  f3:[
    {id:"f3a",name:"Invoices",  type:"folder",modified:"2026-03-20",size:null},
    {id:"f3b",name:"Statements",type:"folder",modified:"2026-03-15",size:null},
    {id:"f3c",name:"Tax 2025.pdf",type:"file",ext:"pdf",modified:"2026-02-28",size:"1.1 MB"},
  ],
  f4:[
    {id:"f4a",name:"Signed NDAs", type:"folder",modified:"2026-03-10",size:null},
    {id:"f4b",name:"Agreements",  type:"folder",modified:"2026-03-05",size:null},
  ],
};

function FolderIco({size=20}){
  return(
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <path d="M2 5.5C2 4.67 2.67 4 3.5 4H8l2 2h6.5c.83 0 1.5.67 1.5 1.5v8c0 .83-.67 1.5-1.5 1.5h-13C2.67 17 2 16.33 2 15.5v-10z" fill="var(--gold,#C9A84C)" fillOpacity=".15" stroke="var(--gold,#C9A84C)" strokeWidth="1.2"/>
    </svg>
  );
}

function FilesModule({lang}){
  const isRTL=lang==="ar";
  const t=T[lang==="ar"?"ar":"en"];
  const platformCtx=useContext(PlatformCtx);
  /* Accounts stored in local component state only — no file content ever touches app state */
  const [accounts,setAccounts]=useState([]);
  const [activeAccount,setActiveAccount]=useState(null);
  const [breadcrumb,setBreadcrumb]=useState([]);
  const [currentItems,setCurrentItems]=useState(MOCK_FS.root);
  const [pinned,setPinned]=useState([]);
  const [showConnect,setShowConnect]=useState(false);
  const [newEmail,setNewEmail]=useState("");
  const [newProvider,setNewProvider]=useState("onedrive");
  const [newLabel,setNewLabel]=useState("");
  const [openFile,setOpenFile]=useState(null);

  const navigate=(item)=>{
    if(item.type!=="folder")return;
    setBreadcrumb(p=>[...p,{id:item.id,name:item.name}]);
    setCurrentItems(MOCK_FS[item.id]||[]);
  };

  const navTo=(idx)=>{
    if(idx===-1){setBreadcrumb([]);setCurrentItems(MOCK_FS.root);return;}
    const c=breadcrumb[idx];
    setBreadcrumb(p=>p.slice(0,idx+1));
    setCurrentItems(MOCK_FS[c.id]||[]);
  };

  const togglePin=(item)=>setPinned(p=>p.find(x=>x.id===item.id)?p.filter(x=>x.id!==item.id):[...p,item]);

  const connect=()=>{
    if(!newEmail.trim())return;
    const prov=FILE_PROVIDERS.find(p=>p.id===newProvider);
    if(prov?.comingSoon){alert(`${localName(prov,lang)} ${t.comingSoonSuffix}`);return;}
    /* Triggers OAuth flow. Command only receives access token — no file data stored. */
    setAccounts(p=>[...p,{id:`acc${Date.now()}`,provider:newProvider,email:newEmail,label:newLabel||newEmail.split("@")[0],color:"#2D7D52"}]);
    setNewEmail("");setNewLabel("");setShowConnect(false);
  };

  const accLabel=accounts.find(a=>a.id===activeAccount)?.label||(platformCtx?.platformModuleNames?.Files||(isRTL?"الملفات":"Files"));

  return(
    <div className="page">
      {/* Security notice */}
      <div style={{background:"var(--g50)",border:"1px solid var(--g200)",borderRadius:"var(--r)",padding:"10px 16px",marginBottom:18,display:"flex",alignItems:"center",gap:10}}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="var(--g400)" strokeWidth="1.4"><rect x="2" y="6" width="10" height="7" rx="1.5"/><path d="M4.5 6V4.5a2.5 2.5 0 015 0V6"/></svg>
        <div style={{fontSize:12,color:"var(--g500)",lineHeight:1.5}}><strong style={{color:"var(--g700)"}}>{t.filesSecurePassthrough}</strong> {t.filesNotStored}</div>
      </div>

      <div className="ph" style={{marginBottom:16}}>
        <div><div className="ptitle">{platformCtx?.platformModuleNames?.Files||(isRTL?"الملفات":"Files")}</div><div className="psub">{platformCtx?.platformModuleSubtitles?.Files||t.filesSubtitle}</div></div>
        <div className="pacts"><button className="btn btn-p" onClick={()=>setShowConnect(true)}>{t.connectAccount}</button></div>
      </div>

      {/* Connect modal */}
      {showConnect&&(
        <div className="mo" onClick={()=>setShowConnect(false)}>
          <div className="md" style={{maxWidth:440}} onClick={e=>e.stopPropagation()}>
            <div className="mh"><div className="mt">{t.connectCloudStorage}</div><button className="btn btn-g btn-sm" onClick={()=>setShowConnect(false)}>✕</button></div>
            <div className="mb">
              <div className="fg" style={{marginBottom:14}}>
                <label className="fl">{t.provider}</label>
                <div style={{display:"flex",gap:8,marginTop:4}}>
                  {FILE_PROVIDERS.map(p=>(
                    <div key={p.id} onClick={()=>!p.comingSoon&&setNewProvider(p.id)} style={{flex:1,border:`2px solid ${newProvider===p.id?p.color:"var(--g200)"}`,borderRadius:"var(--r2)",padding:"12px 8px",cursor:p.comingSoon?"default":"pointer",textAlign:"center",opacity:p.comingSoon?0.4:1,transition:"border-color .12s"}}>
                      <div style={{fontSize:20,color:p.color,marginBottom:5}}>{p.icon}</div>
                      <div style={{fontSize:12,fontWeight:500,color:"var(--ink)"}}>{localName(p, lang)}</div>
                      {p.comingSoon&&<div style={{fontSize:10,color:"var(--g400)",marginTop:2}}>{t.comingSoon}</div>}
                    </div>
                  ))}
                </div>
              </div>
              <div className="fr">
                <div className="fg"><label className="fl">{t.label}</label><input className="fi" value={newLabel} onChange={e=>setNewLabel(e.target.value)} placeholder={t.labelPhWork}/></div>
                <div className="fg" style={{flex:2}}><label className="fl">{t.emailField}</label><input className="fi" value={newEmail} onChange={e=>setNewEmail(e.target.value)} placeholder={t.emailPh} autoFocus/></div>
              </div>
              <div className="notice">
                <div className="notice-l">{t.howItWorks}</div>
                {t.howItWorksFiles}
              </div>
            </div>
            <div className="mf"><button className="btn btn-g" onClick={()=>setShowConnect(false)}>{t.cancel}</button><button className="btn btn-p" onClick={connect}>{t.connectViaOauth}</button></div>
          </div>
        </div>
      )}

      {/* Open file modal */}
      {openFile&&(
        <div className="mo" onClick={()=>setOpenFile(null)}>
          <div className="md" style={{maxWidth:420}} onClick={e=>e.stopPropagation()}>
            <div className="mh"><div className="mt">{t.openFile}</div><button className="btn btn-g btn-sm" onClick={()=>setOpenFile(null)}>✕</button></div>
            <div className="mb">
              <div style={{display:"flex",gap:12,marginBottom:18}}>
                <FileTypeIcon ext={openFile.ext} size={40}/>
                <div><div style={{fontSize:14,fontWeight:600,letterSpacing:"-.01em",marginBottom:3}}>{openFile.name}</div><div style={{fontSize:12,color:"var(--g400)"}}>{openFile.size} · {t.modifiedAt} {openFile.modified}</div></div>
              </div>
              <div className="notice" style={{marginBottom:14}}>
                <div className="notice-l">{t.nativeEditor}</div>
                {t.nativeEditorDesc}
              </div>
              <div style={{display:"flex",gap:8}}>
                <button className="btn btn-p" style={{flex:1}} onClick={()=>{alert(t.openInProviderDemo);setOpenFile(null);}}>
                  {t.openInProvider} {localName(FILE_PROVIDERS.find(p=>p.id===accounts.find(a=>a.id===activeAccount)?.provider),lang)||t.providerFallback}
                </button>
                <button className="btn btn-s" onClick={()=>setOpenFile(null)}>{t.cancel}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{display:"flex",gap:16}}>
        {/* Left sidebar */}
        <div style={{width:206,flexShrink:0,display:"flex",flexDirection:"column",gap:12}}>
          <div className="card">
            <div className="ch"><div className="ct">{t.accounts}</div></div>
            {accounts.map(acc=>{
              const prov=FILE_PROVIDERS.find(p=>p.id===acc.provider);
              return(
                <div key={acc.id} onClick={()=>{setActiveAccount(acc.id);setBreadcrumb([]);setCurrentItems(MOCK_FS.root);}} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderBottom:"1px solid var(--g100)",cursor:"pointer",background:activeAccount===acc.id?"var(--g50)":"transparent",transition:"background .08s"}}>
                  <div style={{width:26,height:26,borderRadius:"50%",background:`${acc.color}18`,border:`1.5px solid ${acc.color}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,color:acc.color,flexShrink:0}}>{prov?.icon}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:activeAccount===acc.id?600:400,color:"var(--ink)"}}>{acc.label}</div>
                    <div style={{fontSize:11,color:"var(--g400)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{acc.email}</div>
                  </div>
                </div>
              );
            })}
            <div style={{padding:"8px 12px"}}><button className="btn btn-g btn-xs" style={{width:"100%"}} onClick={()=>setShowConnect(true)}>{t.addAccount}</button></div>
          </div>

          {pinned.length>0&&(
            <div className="card">
              <div className="ch"><div className="ct">{t.pinned}</div></div>
              {pinned.map(item=>(
                <div key={item.id} onClick={()=>{setBreadcrumb([{id:item.id,name:item.name}]);setCurrentItems(MOCK_FS[item.id]||[]);}} style={{display:"flex",alignItems:"center",gap:9,padding:"8px 14px",borderBottom:"1px solid var(--g100)",cursor:"pointer"}}
                  onMouseEnter={e=>e.currentTarget.style.background="var(--g50)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <FolderIco size={16}/>
                  <div style={{fontSize:13,color:"var(--ink)",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.name}</div>
                </div>
              ))}
            </div>
          )}

          <div style={{background:"var(--g50)",border:"1px solid var(--g100)",borderRadius:"var(--r)",padding:"12px 14px"}}>
            <div style={{fontSize:10,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".08em",marginBottom:8}}>{t.providers}</div>
            {FILE_PROVIDERS.map(p=>(
              <div key={p.id} style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                <span style={{fontSize:14,color:p.comingSoon?"var(--g300)":p.color}}>{p.icon}</span>
                <span style={{fontSize:12,color:p.comingSoon?"var(--g300)":"var(--g600)"}}>{localName(p, lang)}</span>
                <span style={{marginInlineStart:"auto",fontFamily:"var(--fm)",fontSize:9,color:p.comingSoon?"var(--g300)":accounts.some(a=>a.provider===p.id)?"var(--grn)":"var(--g300)"}}>
                  {p.comingSoon?t.statusSoon:accounts.some(a=>a.provider===p.id)?t.statusConnected:"–"}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* File browser */}
        <div style={{flex:1}}>
          {/* Breadcrumbs */}
          <div style={{display:"flex",alignItems:"center",gap:4,marginBottom:12,flexWrap:"wrap"}}>
            <span onClick={()=>navTo(-1)} style={{fontSize:13,color:breadcrumb.length===0?"var(--ink)":"var(--blu)",cursor:"pointer",fontWeight:breadcrumb.length===0?600:400}}>{accLabel}</span>
            {breadcrumb.map((c,i)=>(
              <span key={c.id} style={{display:"flex",alignItems:"center",gap:4}}>
                <span style={{color:"var(--g300)",fontSize:12}}>›</span>
                <span onClick={()=>navTo(i)} style={{fontSize:13,color:i===breadcrumb.length-1?"var(--ink)":"var(--blu)",cursor:"pointer",fontWeight:i===breadcrumb.length-1?600:400}}>{c.name}</span>
              </span>
            ))}
          </div>

          <div className="card">
            <div className="ch">
              <div className="ct">{breadcrumb.length===0?t.rootFolder:breadcrumb[breadcrumb.length-1].name} <span style={{color:"var(--g400)",fontWeight:400}}>({currentItems?.length||0})</span></div>
              {breadcrumb.length>0&&<button className="btn btn-g btn-xs" onClick={()=>navTo(breadcrumb.length-2)}>{t.backNav}</button>}
            </div>
            {!(currentItems?.length)&&<div className="empty">{t.folderEmpty}</div>}
            {(currentItems||[]).map(item=>(
              <div key={item.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 18px",borderBottom:"1px solid var(--g100)",cursor:"pointer",transition:"background .08s"}}
                onMouseEnter={e=>e.currentTarget.style.background="var(--g50)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}
                onClick={()=>{if(item.type==="folder")navigate(item);else setOpenFile(item);}}>
                <div style={{flexShrink:0}}>{item.type==="folder"?<FolderIco size={20}/>:<FileTypeIcon ext={item.ext} size={20}/>}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:14,fontWeight:500,letterSpacing:"-.01em",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.name}</div>
                  <div style={{fontSize:11,color:"var(--g400)",marginTop:2}}>{t.modifiedAt} {item.modified}{item.size?` · ${item.size}`:""}</div>
                </div>
                <div style={{display:"flex",gap:8,alignItems:"center",flexShrink:0}}>
                  {item.type==="folder"&&(
                    <button className="btn btn-g btn-xs" onClick={e=>{e.stopPropagation();togglePin(item);}}>
                      {pinned.find(p=>p.id===item.id)?t.unpinBtn:t.pinBtn}
                    </button>
                  )}
                  {item.type==="file"&&<span style={{fontFamily:"var(--fm)",fontSize:10,color:"var(--g400)"}}>{item.size}</span>}
                  <span style={{color:"var(--g300)",fontSize:16}}>›</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FilesModule;
